/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CluUtteranceSwagger } from 'src/clu/core/services/cluUtteranceSwagger';
import { GptPredictionUtterance } from 'src/clu/core/services/gpt/cluGptSwagger';
import {
    gptPredictedUtteranceSwaggerToGptPredictedUtteranceStore,
    utteranceSwaggerToUtteranceStore
} from 'src/clu/modules/handlers/utterances/cluUtteranceMappers';
import { usePagination } from 'src/core/hooks/usePagination';

type UseCluPaginatedUtterancesParams = { utterances: (CluUtteranceSwagger | GptPredictionUtterance)[]; pageSize: number };

/**
 * A hook for managing the pagination in utterances.
 *
 * @param utterances The total items to be managed.
 * @param pageSize The default page size.
 * @returns The required information to managing the pagination.
 */
export const useCluPaginatedUtterances = (params: UseCluPaginatedUtterancesParams) => {
    const { utterances, pageSize } = params;
    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination(utterances, pageSize);

    return {
        items: pageItems.map(item =>
            item.dataset === 'Generated'
                ? gptPredictedUtteranceSwaggerToGptPredictedUtteranceStore(item as GptPredictionUtterance)
                : utteranceSwaggerToUtteranceStore(item)
        ),
        pageCount,
        currentPage,
        onPageChange: setCurrentPage
    };
};
